import { formatDate, titleCase } from '../BusinessDetailReport/businessReportModel'
import { moreFromLexis } from './moreFromLexis'

const list = (value) => (Array.isArray(value) ? value : value ? [value] : [])
const text = (value) => (value == null || typeof value === 'object' ? '' : String(value)).trim()
const upper = (value) => text(value).toUpperCase()
const dateValue = (date) => (date && typeof date === 'object' && date.Year ? new Date(date.Year, (date.Month || 1) - 1, date.Day || 1).getTime() : 0)
const monthYear = (date) => (date?.Year ? `${date.Month ? `${String(date.Month).padStart(2, '0')}/` : ''}${date.Year}` : '')
const records = (section) => {
    const value = section && typeof section === 'object' ? Object.values(section).find(Array.isArray) : null
    return (value || []).filter(r => r && typeof r === 'object')
}
const isCurrentHistory = (people) => list(people).some(p => /CURRENT/.test(upper(p?.HistoryDescription)))

export function buildVehicles(person, now = new Date()) {
    const vehicles = records(person?.Vehicles).map(v => {
        const info = v.VehicleInfo || {}
        const owners = list(v.Owners?.Owner)
        const registrants = list(v.Registrants?.Registrant)
        const current = typeof v.IsCurrent === 'boolean' ? v.IsCurrent : isCurrentHistory(owners) || isCurrentHistory(registrants)
        const reg = registrants.map(r => r?.RegistrationInfo).filter(Boolean)
            .sort((a, b) => dateValue(b.LatestExpirationDate) - dateValue(a.LatestExpirationDate))[0] || {}
        const liens = list(v.LienHolders?.LienHolder).filter(l => l?.LienHolderInfo?.BusinessName || l?.StandardizedName)
        const currentLiens = liens.filter(l => !/HISTOR/.test(upper(l.HistoryDescription)))
        const lienName = (l) => titleCase(l.StandardizedName || l.LienHolderInfo?.BusinessName)
        const leased = list(v.Lessees?.Lessee).length > 0 || list(v.Lessors?.Lessor).length > 0
        const business = owners.map(o => titleCase(o?.OwnerInfo?.BusinessName)).find(Boolean) || registrants.map(r => titleCase(r?.RegistrantInfo?.BusinessName)).find(Boolean)
        const expired = current && dateValue(reg.LatestExpirationDate) && dateValue(reg.LatestExpirationDate) < now.getTime()
        const weight = Number(text(info.NetWeight).replace(/[^0-9]/g, '')) || 0
        return {
            more: moreFromLexis(v, 'Vehicles/Vehicle[]'),
            current, year: Number(info.YearMake) || 0, lien: current && currentLiens.length > 0,
            title: [info.YearMake, titleCase([info.Make, info.Model || info.Series].filter(Boolean).join(' '))].filter(Boolean).join(' ') || 'Vehicle',
            facts: [titleCase(info.Style || info.Type), info.StateOfOrigin, reg.LatestExpirationDate && `${expired ? 'Registration expired' : 'Registered until'} ${monthYear(reg.LatestExpirationDate)}`]
                .filter(Boolean).join(' · '),
            chips: [
                current && currentLiens[0] && { label: `Lien: ${lienName(currentLiens[0])}`, tone: 'watch' },
                leased && { label: 'Leased', tone: 'neutral' },
                business && { label: `Owned by ${business}`, tone: 'neutral' },
            ].filter(Boolean),
            details: [
                ['VIN', text(info.VIN)],
                ['Plate', [text(reg.LicensePlate), titleCase(reg.LicensePlateTypeDesc)].filter(Boolean).join(' · ')],
                ['Registered', [monthYear(reg.EarliestEffectiveDate || reg.FirstDate), monthYear(reg.LatestExpirationDate)].filter(Boolean).join(' to ')],
                ['Lienholders', liens.map(l => [lienName(l), l.LienDate && `since ${formatDate(l.LienDate)}`, /HISTOR/.test(upper(l.HistoryDescription)) && '(past)']
                    .filter(Boolean).join(' ')).join('; ')],
                ['Weight', weight ? `${weight.toLocaleString('en-US')} lbs` : ''],
                ['Axles', text(info.NumberOfAxles)],
                ['Color', titleCase([info.MajorColor, info.MinorColor].filter(Boolean).join(' / '))],
                ['Use', titleCase(info.VehicleUse)],
                ['Title issued', [formatDate(owners.map(o => o?.TitleInfo?.EarliestIssueDate).find(Boolean)), formatDate(owners.map(o => o?.TitleInfo?.LatestIssueDate).find(Boolean))]
                    .filter(Boolean).filter((v, i, all) => all.indexOf(v) === i).join(' to ')],
                ['Source', titleCase(v.NonDMVSource || v.DataSource)],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    }).sort((a, b) => b.current - a.current || b.year - a.year)

    const boats = records(person?.WaterCrafts).map(w => {
        const d = w.Description || {}
        const r = w.Registration || {}
        return {
            more: moreFromLexis(w, 'WaterCrafts/WaterCraft[]'),
            current: w.IsCurrent !== false,
            title: [d.ModelYear, titleCase([d.Make, d.Model].filter(Boolean).join(' '))].filter(Boolean).join(' ') || 'Boat',
            facts: [titleCase(d.VesselType || d.Use), d.Length && `${text(d.Length)} ft`, r.StateOriginName ? titleCase(r.StateOriginName) : r.StateOrigin,
                w.DateLastSeen && `Last seen ${monthYear(w.DateLastSeen)}`].filter(Boolean).join(' · '),
            chips: [list(w.LienHolders?.LienHolder).length > 0 && { label: 'Lien', tone: 'watch' }].filter(Boolean),
            details: [
                ['Registration', [text(r.Number), titleCase(r.Status)].filter(Boolean).join(' · ')],
                ['Hull', [text(d.HullNumber), titleCase(d.HullType)].filter(Boolean).join(' · ')],
                ['Propulsion', [titleCase(d.Propulsion), titleCase(d.Fuel)].filter(Boolean).join(' · ')],
                ['Purchase price', Number(text(d.PurchasePrice).replace(/[^0-9.]/g, '')) > 0 ? `$${Math.round(Number(text(d.PurchasePrice).replace(/[^0-9.]/g, ''))).toLocaleString('en-US')}` : ''],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    })

    const aircraft = records(person?.Aircrafts).map(a => {
        const info = a.AircraftInfo || {}
        return {
            more: moreFromLexis(a, 'Aircrafts/Aircraft[]'),
            current: true,
            title: titleCase([info.ManufacturerName, info.ModelName].filter(Boolean).join(' ')) || 'Aircraft',
            facts: [a.AircraftNumber && `N-number ${text(a.AircraftNumber)}`, titleCase(info.AircraftType), a.DateLastSeen && `Last seen ${monthYear(a.DateLastSeen)}`]
                .filter(Boolean).join(' · '),
            chips: [a.Status && { label: titleCase(a.Status), tone: 'neutral' }].filter(Boolean),
            details: [
                ['Category', titleCase(info.Category)], ['Engines', [text(info.Engines), titleCase(info.EngineType)].filter(Boolean).join(' · ')],
                ['Seats', text(info.Seats)], ['Certified', formatDate(a.CertificationDate)],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    })

    const certName = (x) => {
        const type = titleCase(x?.TypeDesc || x?.Type)
        const level = titleCase(x?.LevelDesc || x?.Level)
        return level && type && level.toUpperCase().includes(type.toUpperCase()) ? level : [type, level].filter(Boolean).join(', ')
    }
    const pilots = records(person?.FAACertifications).map(c => {
        const certificates = list(c.Certificates?.Certificate).filter(Boolean)
        const medicalExpired = dateValue(c.DateExpiresMedical) && dateValue(c.DateExpiresMedical) < now.getTime()
        return {
            more: moreFromLexis(c, 'FAACertifications/Certification[]'),
            title: certificates.map(certName).filter(Boolean).join(' · ') || 'Pilot certificate',
            facts: [titleCase(c.ClassDesc) || (c.Class && `Medical class ${text(c.Class)}`), c.DateCertifiedMedical && `Medical certified ${formatDate(c.DateCertifiedMedical)}`,
                c.DateExpiresMedical && `${medicalExpired ? 'expired' : 'expires'} ${formatDate(c.DateExpiresMedical)}`].filter(Boolean).join(' · '),
            chips: [medicalExpired && { label: 'Medical expired', tone: 'neutral' }].filter(Boolean),
            details: [
                ...certificates.map((x, n) => [certificates.length > 1 ? `Certificate ${n + 1}` : 'Certificate', [certName(x),
                    text(x.Ratings) && `ratings ${text(x.Ratings).toLowerCase()}`, x.DateExpires && `expires ${formatDate(x.DateExpires)}`].filter(Boolean).join(' · ')]),
                ['Medical certificate', [titleCase(c.ClassDesc) || text(c.Class), formatDate(c.DateCertifiedMedical) && `certified ${formatDate(c.DateCertifiedMedical)}`,
                    formatDate(c.DateExpiresMedical) && `expires ${formatDate(c.DateExpiresMedical)}`].filter(Boolean).join(' · ')],
                ['Record type', titleCase(c.RecordTypeDesc || c.RecordType)],
                ['FAA region', titleCase(c.Region)],
                ['Name on record', titleCase([c.Name?.First, c.Name?.Middle, c.Name?.Last].filter(Boolean).join(' ') || c.Name?.Full)],
            ].filter(([, value]) => value).map(([label, value]) => ({ label, value })),
        }
    })

    const currentVehicles = vehicles.filter(v => v.current)
    return {
        currentVehicles, pastVehicles: vehicles.filter(v => !v.current), boats, aircraft, pilots,
        summary: { current: currentVehicles.length, liens: currentVehicles.filter(v => v.lien).length, past: vehicles.length - currentVehicles.length },
        hasData: vehicles.length + boats.length + aircraft.length + pilots.length > 0,
    }
}
